import { FocusMonitor } from '@angular/cdk/a11y';
import { Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, Optional, Self, ViewChild, computed, inject, signal } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { WorkflowConditionTypes } from 'src/app/components/designer/models/Enums/workflow-condition-types';
import { ActivatedStepsConditionData } from 'src/app/components/designer/models/activated-steps-condition-data';
import { AttributeValueConditionData } from 'src/app/components/designer/models/attribute-value-condition-data';
import { AttributesMustBeFilledInConditionData } from 'src/app/components/designer/models/attributes-must-be-filled-in-condition-data';
import { BaseConditionData } from 'src/app/components/designer/models/base-condition-data';
import { CompletedStepsConditionData } from 'src/app/components/designer/models/completed-steps-condition-data';
import { DefaultConditionData } from 'src/app/components/designer/models/default-condition-data';
import { LogicalExpressionConditionData } from 'src/app/components/designer/models/logical-expression-condition-data';
import { MandatoryAttributesMustBeFilledInConditionData } from 'src/app/components/designer/models/mandatory-attributes-must-be-filled-in-condition-datas';
import { StepSpecificStateConditionData } from 'src/app/components/designer/models/step-specific-state-condition-datas';
import { Workflow } from 'src/app/components/designer/models/workflow';
import { RestApiResponseVariableConfiguration } from 'src/app/models/RestApiCaller/configuration/rest-api-response-variable-configuration';
import { WorkflowConditionFormDialogResult } from './Models/workflow-condition-form-dialog-result';
import { WorkflowContextualMenuActions } from './Models/workflow-contextual-menu-actions';
import { WorkflowConditionFormDialogComponent } from './workflow-condition-form-dialog/workflow-condition-form-dialog.component';

/**
 * Composant de saisie des conditions d'un évènement.
 * (Utilisable dans un formulaire et dans un MatFormField)
 */
@Component({
    selector: 'app-workflow-conditions-form',
    imports: [MatTableModule, MatIconModule, MatMenuModule, MatButtonModule, MatTooltipModule],
    templateUrl: './workflow-condition-form.component.html',
    styleUrl: './workflow-condition-form.component.scss',
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: WorkflowConditionsFormComponent,
        },
    ]
})
export class WorkflowConditionsFormComponent implements OnInit, OnDestroy, MatFormFieldControl<BaseConditionData[]> {
    /**
     * Services utilisés dans le composant.
     */
    private toastrService = inject(ToastrService);

    /**
     * Liste des variables de la réponse.
     */
    @Input()
    public responseVariables: RestApiResponseVariableConfiguration[] = [];

    /**
     * Liste des actions du menu contextuel du tableau des conditions.
     */
    public ContextualMenuActions = WorkflowContextualMenuActions;

    /**
     * Liste des types de conditions et leur libellé.
     */
    public conditionTypes: Map<WorkflowConditionTypes, string> = new Map<WorkflowConditionTypes, string>();

    /**
     * Liste des colonnes affichées dans le tableau des conditions.
     */
    displayedColumns: string[] = ['id', 'name', 'isMainCondition'];

    /**
     * Liste des en-têtes affichées dans le tableau.
     */
    conditions = signal<BaseConditionData[]>([]);

    /**
     * Emet lorsque des changements surviennent dans le composant pour prevenir le MatFormField parent de déclencher le change detection.
     */
    stateChanges = new Subject<void>();

    /**
     * Associe les id des elements dont le contenu sera utilisé pour décrire ce composant à l'attribut aria.
     */
    @HostBinding('attr.aria-describedby') describedBy = '';

    /**
     * Définit les id des elements dont le contenu sera utilisé pour décrire ce composant.
     * @param ids Id des elements dont le contenu sera utilisé pour décrire ce composant.
     */
    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join('');
    }

    /**
     * Définit la valeur du contrôle.
     */
    @Input()
    set value(value: BaseConditionData[]) {
        this.conditions.set(value);
        this.stateChanges.next();
    }

    /**
     * Obtient la valeur du contrôle.
     */
    get value(): BaseConditionData[] {
        return this.conditions();
    }

    private _workflow: Workflow;

    /**
     * Définit l'instance du workflow.
     */
    @Input()
    set workflow(value: Workflow) {
        this._workflow = value;
    }

    /**
     * Définit l'instance du workflow.
     */
    get workflow(): Workflow {
        return this._workflow;
    }

    /**
     * Valeur de l'identifiant qui sera attribuée à la prochaine instance du contrôle.
     */
    static nextId = 0;

    /**
     * Définit la valeur du placeholder.
     */
    set placeholder(placeholder: string) {
        // Le placeholder n'est pas utilisé dans ce composant
    }

    /**
     * Obtient la valeur du placeholder.
     */
    get placeholder() {
        return null;
    }

    /**
     * Retourne une valeur indiquant si le contrôle est vide (absence d'en-tête)
     */
    get empty(): boolean {
        return this.conditions().length !== 0;
    }

    /**
     * Retourne une valeur indiquant si le libellé doit être flottant.
     * (non géré)
     */
    get shouldLabelFloat(): boolean {
        return false;
    }

    /**
     * Obtient une valeur indiquant si ce contrôle est obligatoire.
     */
    @Input()
    get required() {
        return this._required;
    }

    /**
     * Définit une valeur indiquant si ce contrôle est obligatoire.
     */
    set required(value: boolean) {
        this._required = value;
        this.stateChanges.next();
    }

    /**
     * Valeur indiquant si le contrôle est obligatoire.
     */
    private _required = false;

    /**
     * Obtient une valeur indiquant si ce contrôle est désactivé.
     */
    @Input()
    get disabled() {
        return this._disabled;
    }

    /**
     * Définit une valeur indiquant si ce contrôle est désactivé.
     */
    set disabled(value: boolean) {
        this._disabled = value;

        let actionColumnIndex = this.displayedColumns.findIndex((c) => c === 'actions');

        if (value === false) {
            if (actionColumnIndex === -1) {
                this.displayedColumns.push('actions');
            }
        } else {
            if (actionColumnIndex !== -1) {
                this.displayedColumns.splice(actionColumnIndex, 1);
            }
        }
    }

    /**
     * Valeur indiquant si ce contrôle est désactivé.
     */
    private _disabled = false;

    /**
     * Valeur indiquant si le contrôle possède le focus.
     */
    _focused: boolean = false;

    /**
     * Obtient une valeur indiquant si le contrôle possède le focus.
     */
    get focused() {
        return this._focused;
    }

    /**
     * Définit une valeur indiquant si le contrôle possède le focus.
     */
    set focused(value: boolean) {
        this._focused = value;
        this.onTouched(value);
    }

    /**
     * Id du contrôle.
     */
    @HostBinding()
    id = `WorkflowConditionsFormComponent-${WorkflowConditionsFormComponent.nextId++}`;

    /**
     * Réference à l'élément MatTable du template.
     */
    @ViewChild(MatTable, { read: ElementRef, static: true })
    table: ElementRef;

    /**
     * Constructeur.
     * @param focusMonitor : Service utilisé pour écouter les changements de focus.
     * @param dialog : Service utilisé pour ouvrir des boites de dialogue.
     * @param ngControl : Reference au contrôle dans le formulaire.
     */
    constructor(
        private focusMonitor: FocusMonitor,
        public dialog: MatDialog,
        @Optional() @Self() public ngControl: NgControl
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
        computed(() => console.log('conditions', this.conditions()));

        this.conditionTypes.set(WorkflowConditionTypes.AttributeValue, "La valeur d'un attribut doit respecter une condition");
        this.conditionTypes.set(WorkflowConditionTypes.AttributesMustBeFilledIn, 'Des attributs doivent être renseignés');
        this.conditionTypes.set(WorkflowConditionTypes.LogicalExpression, 'Une expression logique soit être respectée');
        this.conditionTypes.set(WorkflowConditionTypes.MandatoryAttributesMustBeFilledIn, 'Les attributs obligatoires doivent être renseignés');
        this.conditionTypes.set(WorkflowConditionTypes.StepActivated, 'Des étapes doivent être actives');
        this.conditionTypes.set(WorkflowConditionTypes.StepCompleted, 'Des étapes doivent être closes');
        this.conditionTypes.set(WorkflowConditionTypes.StepSpecificState, 'Une étape doit être dans un état particulier');
    }

    /**
     * Renseigne la valeur du contrôle.
     * (Appellé par le formulaire)
     * @param value : valeur du contrôle.
     */
    writeValue(value: BaseConditionData[]): void {
        this.value = value;
    }

    /**
     * Fonction à appeler lorsque la valeur du contrôle change.
     */
    private onChange;

    /**
     * Inscrit une fonction à appeler lorsque la valeur du contrôle change.
     * (Appellé par le formulaire)
     * @param fn fonction à appeler lorsque la valeur du contrôle change.
     */
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    /**
     * Fonction à appeler lorsque le contrôle gagne le focus.
     */
    private onTouched;

    /**
     * Inscrit une fonction à appeler lorsque le contrôle gagne le focus.
     * (Appellé par le formulaire)
     * @param fn fonction à appeler lorsque le contrôle gagne le focus.
     */
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /**
     * Specifie l'état 'désactivé' du contrôle.
     * (Appellé par le formulaire)
     * @param value : Valeur de l'état 'désactivé'.
     */
    setDisabledState(value: boolean): void {
        this.disabled = value;
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le contrôle.
     * (Appellé par le formulaire)
     * @param event evnement clic de la souris.
     */
    onContainerClick(event: MouseEvent): void {
        // On ne fait rien de particulier lorsque l'on clique sur le tableau.
    }

    /**
     * Retourne une valeur indiquant si le contrôle est en erreur.
     */
    get errorState(): boolean {
        // On considère que ce control n'est jamais en erreur.
        return false;
    }

    /**
     * Appellé lors de l'initialisation du contrôle.
     */
    ngOnInit(): void {
        this.focusMonitor.monitor(this.table).subscribe((focused) => {
            this.focused = !!focused;
            this.stateChanges.next();
        });
    }

    /**
     * Appellé lors de la destruction du contrôle.
     */
    ngOnDestroy(): void {
        this.focusMonitor.stopMonitoring(this.table);
        this.stateChanges.complete();
    }

    /**
     * Appelé lorsque l'utilisateur double-clique sur un élément de la liste.
     */
    onMouseDoubleClick(row: BaseConditionData, event) {
        console.log('double clic', row);
        if (this.disabled) {
            this.displayCondition(row);
        } else {
            this.onEditCondition(row);
        }
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Editer' du menu contextuel.
     * @param editedCondition : Instance de la condition a éditer.
     */
    public onEditCondition(editedCondition: BaseConditionData): void {
        console.log('edit', editedCondition);

        const dialogRef = this.dialog.open(WorkflowConditionFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: 'Édition de la condition',
                item: editedCondition,
                workflow: this.workflow,
                //variables: this.responseVariables,
                referencedConditions: this.conditions(),
                readonly: this.disabled,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: WorkflowConditionFormDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                // Mise à jour de la condition.
                console.log('update condition', dialogResult.item);

                this.conditions.update((conditions) => {
                    let updatedConditions: BaseConditionData[] = [];

                    // Réalise une copie des conditions existantes.
                    conditions.forEach((x) => {
                        updatedConditions.push(this.cloneCondition(x));
                    });

                    // Met à jour la condition modifiée.
                    const conditionIndex = updatedConditions.findIndex((x) => x.id === editedCondition.id);
                    updatedConditions.splice(conditionIndex, 1, dialogResult.item);

                    return updatedConditions;
                });

                this.onChange(this.conditions());
            }
        });
    }

    /**
     * Affiche une condition en lecture seule.
     * @param condition : Instance de la condition a afficher.
     */
    public displayCondition(condition: BaseConditionData): void {
        console.log('display', condition);

        const dialogRef = this.dialog.open(WorkflowConditionFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: 'Consultation de la condition',
                item: condition,
                workflow: this.workflow,
                readonly: this.disabled,
                //variables: this.responseVariables,
                referencedConditions: this.conditions(),
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        //dialogRef.afterClosed().subscribe((dialogResult: ConditionFormDialogResult) => {

        // });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton de création d'une condition.
     */
    public onCreateCondition(): void {
        console.log('create');

        const dialogRef = this.dialog.open(WorkflowConditionFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Ajout d'une condition",
                item: null,
                workflow: this.workflow,
                //variables: this.responseVariables,
                referencedConditions: this.conditions(),
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: WorkflowConditionFormDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                // Création de la condition.
                console.log('create condition', dialogResult.item);
                dialogResult.item.id = this.getNextId();

                // S'il s'agit de la première condition alors elle est désigné comme principale.
                if (this.conditions().length === 0) {
                    dialogResult.item.isMainCondition = true;
                }

                this.conditions.update((conditions) => {
                    let updatedConditions: BaseConditionData[] = [];

                    // Réalise une copie des conditions existantes.
                    conditions.forEach((x) => {
                        updatedConditions.push(this.cloneCondition(x));
                    });

                    // Ajoute la condition créée.
                    updatedConditions.push(dialogResult.item);

                    return updatedConditions;
                });

                this.onChange(this.conditions());
            }
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Dupliquer' du menu contextuel.
     * @param conditionToDuplicate : Instance de la condition a dupliquer.
     */
    public onDuplicateCondition(conditionToDuplicate: BaseConditionData): void {
        console.log('duplicate');

        let duplicatedCondition = this.cloneCondition(conditionToDuplicate);
        duplicatedCondition.id = null;
        duplicatedCondition.isMainCondition = false;

        const dialogRef = this.dialog.open(WorkflowConditionFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Ajout d'une condition",
                item: duplicatedCondition,
                workflow: this.workflow,
                //variables: this.responseVariables,
                referencedConditions: this.conditions(),
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: WorkflowConditionFormDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                // Création de la condition.
                console.log('create condition', dialogResult.item);
                dialogResult.item.id = this.getNextId();

                // S'il s'agit de la première condition alors elle est désigné comme principale.
                if (this.conditions().length === 0) {
                    dialogResult.item.isMainCondition = true;
                }

                this.conditions.update((conditions) => {
                    let updatedConditions: BaseConditionData[] = [];

                    // Réalise une copie des conditions existantes.
                    conditions.forEach((x) => {
                        updatedConditions.push(this.cloneCondition(x));
                    });

                    // Ajoute la condition créée.
                    updatedConditions.push(dialogResult.item);

                    return updatedConditions;
                });

                this.onChange(this.conditions());
            }
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Supprimer' du menu contextuel.
     * @param event : Instance de la condition a supprimer.
     */
    public onDeleteCondition(event: BaseConditionData): void {
        console.log('delete', event);

        if (event.isMainCondition === true) {
            // Cas de la condition principale.
            if (this.conditions().length > 1) {
                // Il ne s'agit pas de la seule condition --> on ne peut pas supprimer la condition
                this.toastrService.warning(
                    'Vous ne pouvez pas supprimer la condition principale. Veuillez désigner une autre condition comme principale avant de supprimer celle-ci!',
                    'Suppression impossible'
                );

                return;
            }
        }

        this.conditions.update((conditions) => conditions.filter((condition) => condition.id !== event.id));

        this.onChange(this.conditions());
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Désigner comme condition principale' du menu contextuel.
     * @param event : Instance de la condition a marquer.
     */
    public onMarkConditionAsMain(event: BaseConditionData): void {
        console.log('MarkAsMain', event);
        this.conditions.update((conditions) => {
            let updatedConditions: BaseConditionData[] = [];

            // Réalise une copie des conditions existantes.
            conditions.forEach((x) => {
                updatedConditions.push(this.cloneCondition(x));
            });

            const previousMarkIndex = updatedConditions.findIndex((condition) => condition.isMainCondition === true);

            if (previousMarkIndex !== -1) {
                updatedConditions[previousMarkIndex].isMainCondition = false;
            }

            const newMarkIndex = updatedConditions.findIndex((condition) => condition.id === event.id);
            updatedConditions[newMarkIndex].isMainCondition = true;

            return updatedConditions;
        });

        this.onChange(this.conditions());
    }

    /**
     * Retourne l'id autogénéré pour la condition.
     * @returns un Id.
     */
    private getNextId() {
        let nextId = 1;

        if (this.conditions().length !== 0) {
            nextId = this.conditions().reduce((c1, c2) => (c1.id > c2.id ? c1 : c2)).id + 1;
        }

        return nextId;
    }

    private cloneCondition(condition: BaseConditionData): BaseConditionData {
        if (condition) {
            switch (condition.type) {
                case WorkflowConditionTypes.None:
                    return DefaultConditionData.clone(condition as DefaultConditionData);
                case WorkflowConditionTypes.AttributeValue:
                    return AttributeValueConditionData.clone(condition as AttributeValueConditionData);
                case WorkflowConditionTypes.AttributesMustBeFilledIn:
                    return AttributesMustBeFilledInConditionData.clone(condition as AttributesMustBeFilledInConditionData);
                case WorkflowConditionTypes.LogicalExpression:
                    return LogicalExpressionConditionData.clone(condition as LogicalExpressionConditionData);
                case WorkflowConditionTypes.MandatoryAttributesMustBeFilledIn:
                    return MandatoryAttributesMustBeFilledInConditionData.clone(condition as MandatoryAttributesMustBeFilledInConditionData);
                case WorkflowConditionTypes.StepActivated:
                    return ActivatedStepsConditionData.clone(condition as ActivatedStepsConditionData);
                case WorkflowConditionTypes.StepCompleted:
                    return CompletedStepsConditionData.clone(condition as CompletedStepsConditionData);
                case WorkflowConditionTypes.StepSpecificState:
                    return StepSpecificStateConditionData.clone(condition as StepSpecificStateConditionData);
                default:
                    console.error(`Le type de condition '${condition.type}' n'est pas implémentée.`);
                    throw new Error(`Le type de condition '${condition.type}' n'est pas implémentée.`);
            }
        } else {
            return null;
        }
    }
}
